import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {getCalApi} from '@calcom/embed-react'
import {Box} from '@mui/material'
import {
  getProductLineColors,
  useProductLine,
} from '@progynyglobal/pg-ui-library'

import AlertSnackBar from '@progyny/components/AlertSnackBar/AlertSnackBar'
import BookingStepper, {
  type BookingStep,
} from '@progyny/components/CalendarEmbed/BookingStepper'
import CalStep from '@progyny/components/CalendarEmbed/CalStep'
import LanguageStep from '@progyny/components/CalendarEmbed/LanguageStep'
import {useCalEvent} from '@progyny/hooks/useCalEvent'
import {useEarliestSlotForLanguage} from '@progyny/hooks/useEarliestSlotForLanguage'
import {useResolvedCalLinkParams} from '@progyny/hooks/useResolvedCalLinkParams'
import {showConsultationCancelledToast} from '@progyny/pages/Consultations/components/ConsultationToast'
import {useLanguageCriteria} from '@progyny/store/languageStore'
import type {BookingField} from '@progyny/types/calcom'
import {REACT_APP_CALCOM_EMBED_URL} from '@progyny/utils/env'

// Fallback in case Cal.com never fires a success/failure event after submit.
const SUBMIT_TIMEOUT_MS = 15000

interface BookingSuccessfulData {
  confirmed: boolean
  eventType: object
  date: string
  duration: number
  organizer: object
  booking?: {
    uid?: string
    location?: string | null
    [key: string]: unknown
  }
}

interface CalendarEmbedProps {
  calLink: string
  title?: string
  description?: string
  height?: string | number
  onBookingSuccessful?: (data: BookingSuccessfulData) => void
  prefillData?: {
    email?: string
    name?: string
    [key: string]: string | undefined
  }
  metadata?: Record<string, string>
  rescheduleUid?: string
  additionalContent?: string
  minDate?: string
  maxDate?: string
  routedTeamMemberIds?: number[]
  language?: string
  languageLocked?: boolean
  bookingFields?: BookingField[]
  lockAttendeeFields?: boolean
  routingFormResponses?: Record<string, string>
  startingStep?: 1 | 2 | 3 | 4
  onBack: () => void
  onDone?: () => void
}

const CalendarEmbed: React.FC<CalendarEmbedProps> = ({
  calLink,
  title,
  description,
  height,
  onBookingSuccessful,
  prefillData,
  metadata,
  rescheduleUid,
  additionalContent,
  minDate,
  maxDate,
  routedTeamMemberIds,
  language,
  languageLocked,
  bookingFields,
  lockAttendeeFields,
  routingFormResponses,
  startingStep = 1,
  onBack,
  onDone,
}) => {
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const {t} = useTranslation()

  const {defaultLanguage} = useLanguageCriteria()

  const [calendarLoaded, setCalendarLoaded] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [step, setStep] = useState<1 | 2 | 3 | 4>(startingStep)
  const [selectedLanguage, setSelectedLanguage] = useState(language ?? 'en')
  const [canContinue, setCanContinue] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const earliestSlotSearchParams = useResolvedCalLinkParams({
    calLink,
    routingFormResponses,
    language: selectedLanguage,
    minDate,
    maxDate,
    enabled: !languageLocked && step === 1,
  })

  const {earliestEnglishSlot, earliestSelectedLanguageSlot} =
    useEarliestSlotForLanguage(earliestSlotSearchParams)

  const handleLanguageChange = (newLanguage: string) => {
    setSelectedLanguage(newLanguage)
  }
  const calApiRef = useRef<((...args: any[]) => void) | null>(null)
  const isCalStepVisible = step !== 1
  const calStepWasVisible = useRef(isCalStepVisible)
  const submitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearSubmitTimeout = () => {
    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current)
      submitTimeoutRef.current = null
    }
  }

  const stopSubmitting = () => {
    clearSubmitTimeout()
    setIsSubmitting(false)
  }

  useEffect(() => clearSubmitTimeout, [])

  const stepperSteps: BookingStep[] = [
    {step: 1, label: t('consultations_calendar_embed_step_choose_language')},
    {step: 2, label: t('consultations_calendar_embed_step_pick_a_time')},
    {
      step: 3,
      label: t('consultations_calendar_embed_step_review_your_details'),
    },
    {step: 4, label: t('consultations_calendar_embed_step_confirmation')},
  ]

  // Add routing parameters to calLink if provided, and selected UI language param.
  const enhancedCalLink = React.useMemo(() => {
    const separator = calLink.includes('?') ? '&' : '?'

    let linkWithLanguage = `${calLink}${separator}uiLang=${defaultLanguage}`

    // Forward the same Contentful preview token member-portal's own i18n.ts reads,
    // so the embedded Cal.com booking page can preview draft translation strings too.
    const previewToken = new URLSearchParams(window.location.search).get(
      'preview'
    )
    if (previewToken) {
      linkWithLanguage += `&preview=${encodeURIComponent(previewToken)}`
    }

    if (!routedTeamMemberIds || routedTeamMemberIds.length === 0) {
      return linkWithLanguage
    }
    return `${linkWithLanguage}&cal.routedTeamMemberIds=${routedTeamMemberIds.join(',')}`
  }, [calLink, routedTeamMemberIds, defaultLanguage])

  const namespace = enhancedCalLink.replace('/', '-')

  // Convert metadata to Cal.com format: metadata[key] = value
  // Include language in metadata so it's available in the booking webhook
  const metadataWithLanguage = {
    ...metadata,
    ...(selectedLanguage && {language: selectedLanguage}),
  }

  const calcomMetadata = metadataWithLanguage
    ? Object.entries(metadataWithLanguage).reduce(
        (acc, [key, value]) => {
          acc[`metadata[${key}]`] = value
          return acc
        },
        {} as Record<string, string>
      )
    : {}

  // Convert routing form responses to Cal.com format
  const calcomRoutingFormResponses = routingFormResponses
    ? Object.entries(routingFormResponses).reduce(
        (acc, [key, value]) => {
          acc[key] = value
          return acc
        },
        {} as Record<string, string>
      )
    : {}

  // Encode additional content HTML for Cal.com embed
  // Wrap in styled div to ensure proper rendering
  const encodedAdditionalContent = additionalContent
    ? encodeURIComponent(
        `<div style="margin: 8px 0; line-height: 1.6;">${additionalContent}</div>`
      )
    : undefined

  // Create a key that changes when title or description changes to force remount.
  const embedKey = React.useMemo(
    () =>
      `${namespace}-${title || ''}-${description || ''}-${selectedLanguage || ''}`,
    [namespace, title, description, selectedLanguage]
  )

  useEffect(() => {
    setCalendarLoaded(false)
  }, [embedKey])

  // CalStep unmounts when stepping back to step 1 (language step) and
  // remounts a fresh Cal iframe when returning - reset the loader for that
  // transition too, since embedKey alone doesn't change in that case.
  useEffect(() => {
    if (isCalStepVisible && !calStepWasVisible.current) {
      setCalendarLoaded(false)
    }
    calStepWasVisible.current = isCalStepVisible
  }, [isCalStepVisible])

  useCalEvent(namespace, 'bookingCancelled', () => {
    onBack()
    showConsultationCancelledToast()
  })

  useEffect(() => {
    ;(async function () {
      try {
        const cal = await getCalApi({
          namespace,
          embedJsUrl: REACT_APP_CALCOM_EMBED_URL,
        })
        calApiRef.current = cal

        cal('ui', {
          hideEventTypeDetails: false,
          layout: 'month_view',
          theme: 'light',
          cssVarsPerTheme: {
            light: {
              'cal-border-booker-width': '0px',
              'cal-brand': colors.primary,
              'cal-brand-text': colors.primaryLight,
            },
            dark: {},
          },
          styles: {
            branding: {
              brandColor: colors.primary,
            },
          },
          holdBookingForm: true,
          hideFormButtons: true,
        } as any)

        cal('on', {
          action: 'linkReady',
          callback: () => {
            setCalendarLoaded(true)
          },
        })

        cal('on', {
          action: 'linkFailed',
          callback: (e: any) => {
            console.error('Cal.com embed link failed:', e) // eslint-disable-line no-console
            setCalendarLoaded(true)
            setSnackbarOpen(true)
          },
        })

        // Listen for booking success (new bookings)
        cal('on', {
          action: 'bookingSuccessful',
          callback: (e: any) => {
            const {data} = e.detail
            stopSubmitting()
            setStep(4)
            onBookingSuccessful?.(data)
          },
        })

        // Listen for reschedule success
        cal('on', {
          action: 'rescheduleBookingSuccessful',
          callback: (e: any) => {
            const {data} = e.detail
            stopSubmitting()
            setStep(4)
            onBookingSuccessful?.(data)
          },
        })

        cal('on', {
          action: '__bookerStateChanged' as 'linkReady',
          callback: (e: any) => {
            const {bookerState} = e.detail?.data ?? {}
            if (bookerState === 'booking') {
              setCanContinue(true)
            } else if (
              bookerState === 'selecting_time' ||
              bookerState === 'selecting_date'
            ) {
              setCanContinue(false)
              stopSubmitting()
              setStep((prev) => (prev === 3 ? 2 : prev))
              calApiRef.current?.('ui', {holdBookingForm: true})
            }
          },
        })

        // Step that only happens via Cal.com's native Reschedule link
        cal('on', {
          action: 'navigatedToBooker',
          callback: () => {
            setStep((prev) => (prev === 4 ? 2 : prev))
          },
        })
      } catch (error) {
        console.error('Cal.com embed initialization error:', error) // eslint-disable-line no-console
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace, onBookingSuccessful, colors])

  const handleContinue = () => {
    if (step === 2) {
      if (!canContinue) {
        return
      }
      calApiRef.current?.('ui', {holdBookingForm: false})
      setStep((prev) => (prev < 4 ? ((prev + 1) as 1 | 2 | 3 | 4) : prev))
      return
    }
    if (step === 3) {
      setIsSubmitting(true)
      clearSubmitTimeout()
      submitTimeoutRef.current = setTimeout(stopSubmitting, SUBMIT_TIMEOUT_MS)
      calApiRef.current?.('ui', {submitBookingFormRequestId: Date.now()})
      return
    }
    setStep((prev) => (prev < 4 ? ((prev + 1) as 1 | 2 | 3 | 4) : prev))
  }

  const handleBackStep = () => {
    if (step === 3) {
      calApiRef.current?.('ui', {holdBookingForm: true})
      stopSubmitting()
      setStep(2)
      return
    }
    setStep((prev) => (prev > 1 ? ((prev - 1) as 1 | 2 | 3 | 4) : prev))
  }

  const handleBackConsultations = () => {
    onBack()
    setStep(1)
  }

  return (
    <Box>
      <AlertSnackBar
        open={snackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        type='error'
        message={t('calendar_embed_load_error')}
      />
      <BookingStepper
        steps={stepperSteps}
        activeStep={step}
        onBack={handleBackConsultations}
        onContinue={handleContinue}
        onBackStep={handleBackStep}
        continueDisabled={step === 2 && !canContinue}
        continueLoading={isSubmitting}
        onDone={onDone}
      >
        {step === 1 && (
          <LanguageStep
            value={selectedLanguage}
            onChange={handleLanguageChange}
            earliestSlotForLanguage={earliestEnglishSlot}
            earliestSelectedLanguageSlot={earliestSelectedLanguageSlot}
          />
        )}
        {(step === 2 || step === 3 || step === 4) && (
          <CalStep
            embedKey={embedKey}
            namespace={namespace}
            enhancedCalLink={enhancedCalLink}
            height={height}
            rescheduleUid={rescheduleUid}
            title={title}
            description={description}
            encodedAdditionalContent={encodedAdditionalContent}
            minDate={minDate}
            maxDate={maxDate}
            selectedLanguage={selectedLanguage}
            languageLocked={languageLocked}
            lockAttendeeFields={lockAttendeeFields}
            prefillData={prefillData}
            calcomMetadata={calcomMetadata}
            calcomRoutingFormResponses={calcomRoutingFormResponses}
            bookingFields={bookingFields}
            calendarLoaded={calendarLoaded}
            productLine={productLine}
          />
        )}
      </BookingStepper>
    </Box>
  )
}

export default CalendarEmbed
